import React from 'react'
import { useMediaQuery } from './helpers/use-media-query'
import { createPortal } from 'react-dom'
import { twMerge } from 'tailwind-merge'
import { Transition } from 'headless-primitives'
import { useBodyScrollLock } from './helpers/use-body-lock-scroll'
import { composeRefs } from '~/helpers/compose-refs'

export interface MobileModalProps extends Omit<React.ComponentProps<'div'>, 'className' | 'children'> {
  className?: string | ((params: { isPortal: boolean }) => string | undefined)
  children?: React.ReactNode | ((params: { isPortal: boolean }) => React.ReactNode)
  open: boolean
  disablePortal?: boolean
  afterLeave?: () => void
}

export const MobileModal = React.forwardRef<HTMLDivElement, MobileModalProps>(
  ({ className, children, open, disablePortal = true, afterLeave, ...props }, ref) => {
    const containerRef = React.useRef<HTMLDivElement>(null)
    const scrollLocker = useBodyScrollLock(containerRef)
    const isSmall = useMediaQuery('(max-width: 1279px)')

    if (typeof document !== 'undefined' && isSmall && disablePortal) {
      return (
        <>
          {createPortal(
            <Transition
              ref={composeRefs(containerRef, ref)}
              show={open}
              afterEnter={() => scrollLocker.lock()}
              afterLeave={() => {
                scrollLocker.unlock()
                afterLeave?.()
              }}
              className="fixed inset-0 isolate z-50"
              as="div"
            >
              <Transition.Child
                enter="ease-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
                as={React.Fragment}
              >
                <div className="bg-gray-500/75 w-full h-full" />
              </Transition.Child>
              <Transition.Child
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                className="fixed inset-0 bg-none"
                as="div"
              >
                {/* Inset the sheet from the device safe areas with padding (env() in `padding`, which
                    WebKit resolves reliably) rather than positioning the box with `inset-safe` (env()
                    in `top`/inset on a fixed element, which resolves to 0 in the Capacitor iOS WebView —
                    leaving the sheet under the Dynamic Island / home indicator). #25115 */}
                <div className="h-full w-full p-safe">
                  <div
                    {...props}
                    className={twMerge(typeof className === 'function' ? className({ isPortal: true }) : className)}
                  >
                    {typeof children === 'function' ? children({ isPortal: true }) : children}
                  </div>
                </div>
              </Transition.Child>
            </Transition>,
            document.body
          )}
        </>
      )
    } else {
      return (
        <Transition
          show={open}
          as={React.Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          afterLeave={() => afterLeave?.()}
        >
          <div {...props} className={typeof className === 'function' ? className({ isPortal: false }) : className} ref={ref}>
            {typeof children === 'function' ? children({ isPortal: false }) : children}
          </div>
        </Transition>
      )
    }
  }
)
MobileModal.displayName = 'MobileModal'
